import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  updateItem,
  setOpenItem,
  fetchPlan,
  deleteItem,
} from "shared/itemLists/redux/itemListsSlice";

import BackButton from "globalComponents/BackButton";
import ItemDetails from "./ItemDetails/ItemDetails";
import ItemCalendarView from "./ItemCalendarView";
import { DateHelper } from "shared/helpers/DateHelper";

function ItemView(props) {
  const {
    setOpenItem,
    openItem,
    dateHelper,
    updateItem,
    deleteItem,
    setOpenChecklist,
  } = props;

  const [itemType, setItemType] = useState(startingItemType());
  const [itemName, setItemName] = useState(openItem.details.name);
  const [itemDone, setItemDone] = useState(openItem.isDone);
  const [itemTime, setItemTime] = useState(openItem.details.time);
  const [appointmentDuration, setAppointmentDuration] = useState(
    startingAppointmentDuration()
  );
  const [itemDay, setItemDay] = useState(startingItemDay);
  const [taskType, setTaskType] = useState(openItem.details.task_type);
  const [colorType, setColorType] = useState(
    openItem.details.color_type || "white"
  );
  const [itemDescription, setItemDescription] = useState(
    openItem.details.description
  );
  const [activeDate, setActiveDate] = useState(startingActiveDate());
  const [calendarNotes, setCalendarNotes] = useState([]);
  const [itemHasChecklist, setItemHasChecklist] = useState(
    openItem.details.has_checklist
  );
  const [isDue, setIsDue] = useState(openItem.isDue);

  useEffect(() => {
    window.addEventListener("beforeunload", onBeforeUnload);
    return function cleanup() {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (itemType === "appointment" && !itemTime) {
      setItemTime("12:00p");
    }
    if (itemType !== "calendar_note") {
      setCalendarNotes([]);
    }
    if (
      itemType === "calendar_note" &&
      activeDate &&
      activeDate.getTime() === itemDay.getTime()
    ) {
      setActiveDate(null);
    }
  }, [itemType]);

  useEffect(() => {
    if (itemType === "calendar_note") {
      if (activeDate) {
        const days = DateHelper.dayRange(activeDate, itemDay);
        const newCNs = days.map((day, i) => {
          let cn_type = i === 0 ? "calendar_note" : "continuing_calendar_note";
          return {
            type: cn_type,
            color_type: colorType,
            duration: days.length,
            current_day: i + 1,
            id: "unsaved_cn",
            day: DateHelper.dateString(day),
          };
        });
        setCalendarNotes(newCNs);
      } else {
        setCalendarNotes([
          {
            type: "calendar_note",
            color_type: colorType,
            duration: 1,
            id: "unsaved_cn",
            day: DateHelper.dateString(itemDay),
          },
        ]);
      }
    }
  }, [itemType, colorType, itemDay, activeDate]);

  useEffect(() => {
    if (isDue && itemType !== "task") {
      setIsDue(false);
      setItemDay(dateHelper.date);
    }
  }, [itemType]);

  function onBeforeUnload(event) {
    event.preventDefault();
    event.returnValue = "saved";
  }

  function handleBackButtonClick() {
    openItem.setIsItemSwipedLeft(false);
    setOpenItem((currOI) => {
      return { ...currOI, isOpen: false };
    });
    const atu = attributesToUpdate();
    if (Object.keys(atu).length > 0) {
      updateItem({
        attributes: atu,
        isDueItem: isDue,
        id: openItem.details.id,
      });
    }
  }

  function handleDeleteClick() {
    if (window.confirm("Are you sure you want to delete?")) {
      deleteItem({ isDueItem: isDue, method: { type: "openItem" } });
    }
  }

  function startingAppointmentDuration() {
    if (itemType === "appointment") {
      return openItem.details.duration;
    } else {
      return null;
    }
  }

  function startingItemType() {
    let t = openItem.details.type;
    if (t === "continuing_calendar_note") {
      t = "calendar_note";
    }
    return t;
  }

  function startingItemDay() {
    if (itemType === "calendar_note") {
      return DateHelper.interval(
        startingActiveDate(),
        openItem.details.duration - 1,
        "days"
      );
    }
    return DateHelper.parseDate(openItem.details.day);
  }

  function startingActiveDate() {
    if (itemType === "calendar_note") {
      return DateHelper.parseDate(openItem.details.day);
    } else {
      const dateString = openItem.details.active_date;
      if (dateString) {
        return DateHelper.parseDate(dateString);
      } else {
        return null;
      }
    }
  }

  function attributesToUpdate() {
    const latest = newItem();
    const atts = {};
    const oldItem = { done: openItem.isDone, ...openItem.details };
    for (const attribute in oldItem) {
      if (oldItem[attribute] !== latest[attribute]) {
        atts[attribute] = latest[attribute];
      }
    }
    if (
      itemType === "calendar_note" &&
      openItem.details.type !== "calendar_note"
    ) {
      atts.duration = latest.duration;
      atts.color_type = latest.color_type;
    }
    if (itemType === "appointment" && openItem.details.type !== "appointment") {
      atts.duration = latest.duration;
      atts.time = latest.time;
    }
    if (itemType === "task" && openItem.details.type !== "task") {
      atts.active_date = activeDate ? DateHelper.dateString(activeDate) : null;
    }
    return atts;
  }

  function newItem() {
    const result = {
      ...openItem.details,
      done: itemDone,
      day: DateHelper.dateString(itemDay),
      time: itemTime,
      type: itemType,
      description: itemDescription,
      color_type: colorType,
      task_type: taskType,
      name: itemName,
      active_date: activeDate ? DateHelper.dateString(activeDate) : null,
    };
    if (itemType === "calendar_note") {
      if (activeDate) {
        result.duration = dateHelper.daysBetween(activeDate, itemDay) + 1;
        result.day = DateHelper.dateString(activeDate);
        result.active_date = null;
      } else {
        result.duration = 1;
      }
    }
    if (itemType === "appointment") {
      result.duration = appointmentDuration;
    }
    return result;
  }

  function titleize(string) {
    return string.split("_").reduce((acc, word) => {
      const space = acc === "" ? "" : " ";
      return acc + space + word.charAt(0).toUpperCase() + word.slice(1);
    }, "");
  }

  return (
    <div className="backdrop">
      <div className="pane item-pane">
        <div className="pane-header">
          <BackButton handleBackButtonClick={handleBackButtonClick} />
          <span className="pane-title">{titleize(itemType)}</span>
          <span className="review-icon"></span>
        </div>
        <ItemCalendarView
          itemDay={itemDay}
          setItemDay={setItemDay}
          itemType={itemType}
          taskType={taskType}
          setTaskType={setTaskType}
          dateHelper={dateHelper}
          activeDate={activeDate}
          setActiveDate={setActiveDate}
          calendarNotes={calendarNotes}
          openItem={openItem}
        />
        <ItemDetails
          handleDeleteClick={handleDeleteClick}
          setOpenChecklist={setOpenChecklist}
          itemId={openItem.details.id}
          itemHasChecklist={itemHasChecklist}
          setItemHasChecklist={setItemHasChecklist}
          itemType={itemType}
          setItemType={setItemType}
          itemName={itemName}
          setItemName={setItemName}
          itemDone={itemDone}
          setItemDone={setItemDone}
          itemTime={itemTime}
          setItemTime={setItemTime}
          appointmentDuration={appointmentDuration}
          setAppointmentDuration={setAppointmentDuration}
          itemDescription={itemDescription}
          setItemDescription={setItemDescription}
          taskType={taskType}
          setTaskType={setTaskType}
          colorType={colorType}
          setColorType={setColorType}
          setCalendarNotes={setCalendarNotes}
          itemDay={itemDay}
          setItemDay={setItemDay}
          activeDate={activeDate}
          setActiveDate={setActiveDate}
          dateHelper={dateHelper}
        />{" "}
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    openItem: state.itemLists.openItem,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    { updateItem, setOpenItem, fetchPlan, deleteItem },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ItemView);
